"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import { ControlPosition, Map } from "@vis.gl/react-google-maps"
import { useState } from "react"
import { MapIcon, MapListIcon, Mobile, useIsDesktop } from "ui"
import { extractProperty } from "utils"
import CustomMarker from "./CustomMarker"
import FactoriesList from "./FactoriesList"
import MapLocaleProvider from "./MapLocaleProvider"

const { GOOGLE_API_KEY } = process.env

const StyledContainer = styled.div`
  width: 100vw;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 39.5px;
  }
`

const StyledMapContainer = styled.div`
  position: relative;
  overflow: hidden;
  ${(props) => props.theme.breakpoints.up("md")} {
    border-radius: 20px;
  }
`

const StyledSwitchButtons = styled(Button)`
  padding: 8px 12px;
  display: inline-flex;
  border-radius: 200px;
  margin: 0;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-decoration: none;
  min-width: 96px;
  svg,
  path {
    fill: inherit;
  }
  :hover {
    text-decoration: none !important;
    background-color: white !important;
  }
`

type Props = {
  data: any
  locale: string
}

export type FactoryType = {
  companyAssociation: string
  branchNumber: string
  factoryName: string
  icon: Array<{ id: number; url: string; altText: null | string }>
  mapPinBackground: string
  address: string[]
  mapCoordinates: {
    address: {
      coordinates: { latitude: number; longitude: number; isEmpty: boolean }
      fullAddress: string
      streetNumber: string
      street: string
      postalCode: string
      city: string
      state: string
      country: string
    }
    mapConfig: {
      apiKey: string
      zoom: number
      centerCoordinates: { latitude: number; longitude: number; isEmpty: boolean }
      style: null | string
      mapType: string
    }
  }
  phoneNumber: Array<{
    name: string
    target: string
    type: string
    url: string
  }>
  image: Array<{ id: number; url: string; altText: null | string }>
}

export type BranchType = { companyAssociation: string; companyFactorys: Array<FactoryType> }

export type MapData = Array<BranchType>

const FactoriesMap = ({ data, locale }: Props) => {
  const isDesktop = useIsDesktop()
  const factoryMapItems: MapData = extractProperty(data, "factoryMapItems")?.map(
    (item: any, i: number) => {
      const companyAssociation = extractProperty(item, "companyAssociation")
      const companyFactorys = extractProperty(item, "companyFactorys")?.map(
        (companyFactory: any, j: number) => {
          const branchNumber = `${i}-${j}`
          const companyFactoryProperties = companyFactory?.properties
          const factoryName = companyFactoryProperties?.find(
            (prop: any) => prop.propertyName === "factoryName",
          )?.propertyValue?.value
          const icon = companyFactoryProperties?.find((prop: any) => prop.propertyName === "icon")
            ?.propertyValue?.mediaItems

          const mapPinBackground = companyFactoryProperties?.find(
            (prop: any) => prop.propertyName === "mapPinBackground",
          )?.propertyValue?.value

          const address = companyFactoryProperties
            ?.find((prop: any) => prop.propertyName === "address")
            ?.propertyValue?.links?.map((link: any) => {
              return link.url
            })

          const mapCoordinates = companyFactoryProperties?.find(
            (prop: any) => prop.propertyName === "mapCoordinates",
          )?.propertyValue?.value

          const phoneNumber = companyFactoryProperties?.find(
            (prop: any) => prop.propertyName === "phoneNumber",
          )?.propertyValue?.links

          const image = companyFactoryProperties?.find((prop: any) => prop.propertyName === "image")
            ?.propertyValue?.mediaItems
          return {
            companyAssociation,
            branchNumber,
            factoryName,
            icon,
            mapPinBackground,
            address,
            mapCoordinates,
            phoneNumber,
            image,
          }
        },
      )
      return { companyAssociation, companyFactorys }
    },
  )
  const factoriesTotal = factoryMapItems?.map((factory) => factory.companyFactorys)?.flat()
  const [localSelect, setLocalSelect] = useState<string>("0")
  const [screenType, setScreenType] = useState<"map" | "list">("map")

  return (
    <MapLocaleProvider
      apiKey={GOOGLE_API_KEY || "AIzaSyD2AanBqJJ8WM8JL6LJ3jjbYkUgD4Mk0Cc"}
      locale={locale}
    >
      <StyledContainer>
        <Mobile>
          <Box
            sx={{
              padding: "8px",
              gap: "8px",
              backgroundColor: "#F7F7F7",
              display: "inline-flex",
              borderRadius: "200px",
              margin: "0 16px 24px",
            }}
          >
            <StyledSwitchButtons
              sx={{
                backgroundColor: screenType === "list" ? "transparent" : "white",
                color: screenType === "map" ? "black" : "#6F6F6F",
                fill: screenType === "map" ? "black" : "#6F6F6F",
              }}
              onClick={() => setScreenType("map")}
            >
              <MapIcon />
              {locale === "he" ? "מפה" : "Map"}
            </StyledSwitchButtons>
            <StyledSwitchButtons
              sx={{
                backgroundColor: screenType === "map" ? "transparent" : "white",
                color: screenType === "list" ? "black" : "#6F6F6F",
                fill: screenType === "list" ? "black" : "#6F6F6F",
              }}
              onClick={() => setScreenType("list")}
            >
              <MapListIcon />
              {locale === "he" ? "רשימה" : "List"}
            </StyledSwitchButtons>
          </Box>
        </Mobile>
        <StyledMapContainer
          style={{
            height: !isDesktop && screenType === "list" ? "unset" : "693px",
            maxHeight: !isDesktop && screenType === "list" ? "unset" : "calc(100dvh - 98px)",
          }}
        >
          {((!isDesktop && screenType === "list") || isDesktop) && (
            <FactoriesList
              branches={factoryMapItems}
              localSelect={localSelect}
              setLocalSelect={setLocalSelect}
              setScreenType={setScreenType}
              locale={locale}
            />
          )}
          <Map
            // mapId="4da427c6d0566d02"
            mapId="DEMO_MAP_ID"
            mapTypeControl={false}
            gestureHandling={"cooperative"}
            disableDefaultUI={true}
            zoomControl={true}
            zoomControlOptions={{
              position: isDesktop ? ControlPosition.BOTTOM_LEFT : ControlPosition.TOP_LEFT,
            }}
            defaultCenter={{
              lat: 31.4117257,
              lng: 35.0818155,
            }}
            defaultZoom={8}
          >
            {factoriesTotal?.map((branch, key) => {
              return (
                <CustomMarker
                  key={key.toString()}
                  branch={branch}
                  localSelect={localSelect}
                  setLocalSelect={setLocalSelect}
                />
              )
            })}
          </Map>
        </StyledMapContainer>
      </StyledContainer>
    </MapLocaleProvider>
  )
}
export default FactoriesMap
